<template>
  <div
    class="
      product-grid
      critical-component-clear
    "
    :class="[
      {
        [$class('hidden')]: isMobile ?
          section.hideMobile : section.hideDesktop,
        'product-grid--half': halfWidth,
      },
      `section-content--${section.contentColor}`
    ]"
    :style="`--product-grid-bg: ${section.backgroundColor};`"
  >
    <div
      v-if="section.titleText"
      class="product-grid__title text-desktop-16-uc-desktop text-mobile-16-uc-mobile"
      v-html="section.titleText"
    />

    <div
      v-if="displayItems"
      class="product-grid__grid"
      :class="[
        section.gridClass && section.gridClass,
        section.layout && `product-grid__grid--${section.layout}`,
        {
          [`product-grid__grid--${section.layout}-half`]: halfWidth,
        },
      ]"
    >
      <template
        v-for="(item, index) of displayItems"
        :key="`product-grid__item-${index}`"
      >
        <div
          v-if="item?.blockSettings"
          class="product-grid__item product-grid__item--media"
          :class="[
            {
              [$class('hidden')]: isMobile ?
                item.blockSettings.hideMobile : item.blockSettings.hideDesktop },
            item.mobileMediaSettings.mobileFullRow && 'product-grid__item--full-row-mobile',
            item.desktopMediaSettings.desktopFullRow && 'product-grid__item--full-row-desktop'
          ]"
          :style="[
            `--columns-mobile: ${item.mobileMediaSettings.mobileMediaWidth};`,
            `--columns-desktop: ${item.desktopMediaSettings.desktopMediaWidth};`,
            `--rows-desktop: ${item.desktopMediaSettings.desktopMediaHeight};`,
            `--order: ${(item.blockSettings.mediaOrder)};`
          ]"
        >
          <media-content
            :component-settings="{
              layout: item.blockSettings.mediaLayout,
              hasContent: false,
              autoplay: true,
              muted: true,
              loop: true
            }"
            block-class="product-grid"
            :mobile-media-settings="item.mobileMediaSettings"
            :desktop-media-settings="item.desktopMediaSettings"
            content-placement="half"
            parent="product-grid__item--media"
          />
          <div
            v-if="item.blockSettings.text"
            class="
              product-grid__item-text"
          >
            <a
              v-if="
                item.blockSettings.url"
              :href="item.blockSettings.url"
            >
              <icon-chevron-right />
              <span
                class="text-regular"
                v-text="item.blockSettings.text"
              />
            </a>

            <p
              v-else
            >
              <span
                class="text-regular"

                v-text="item.blockSettings.text"
              />
            </p>
          </div>
        </div>

        <product-card
          v-else-if="item?.handle"
          :product="item"
          :layout="section.layout"
          :show-badge="section.showBadges"
          :show-flat-image="section.showFlatImages"
          :product-order="item?.order"
          class="product-grid__item product-grid__item--product"
          @product-card-image-loaded="removePlaceholders"
        />
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product grid (product-grid)
 * -----------------------------------------------------------------------------
 * Grid component displaying product cards.
 *
 * @param {Object} section - Section settings.
 * @param {Array} items - Items to be displayed in grid.
 * @param {Boolean} halfWidth - Condition indicating if the component
 * should occupy half width.
 *
 */
import { mapState } from 'vuex'
import IconChevronRight from '~icons/direction/chevron/right.svg'
import ProductCard from '~async/product-card/product-card'
import MediaContent from '~async/media-content/media-content'

export default {
  name: 'ProductGrid',

  components: {
    ProductCard,
    MediaContent,
    IconChevronRight,
  },

  props: {
    section: {
      type: Object,
      default: () => ({
        backgroundColor: '',
        collection: {},
        contentColor: '',
        gridClass: '',
        hideDesktop: false,
        hideMobile: false,
        id: '',
        layout: '',
        maxProducts: '',
        titleText: '',
        showBadges: false,
        showFlatImages: true,
      }),
    },
    items: {
      type: Array,
      default: () => [],
    },
    halfWidth: {
      type: Boolean,
      default: false,
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute items to display in grid
     * @returns {Boolean|String}
     */
    displayItems() {
      if (!this.section.collection?.items?.length && !this.items?.length) {
        return false
      }

      // If the section has only media items, merge it with the collection items
      const hasOnlyMediaItems = this.items.length && !this.items.some(item => item.handle)

      if (hasOnlyMediaItems) {
        const items = this.section.collection.items
        items.unshift(this.items)

        return items.flat()
      }

      return this.items?.length
        ? this.items
        : this.section.collection?.items

    },
  },

  methods: {

    /**
     * Removes the liquid placeholders AFTER the vue media has loaded
     * and fully rendered.
     */
    removePlaceholders() {
      this.$nextTick().then(() => {
        const parent = this.$el.closest('section')
        const placeholder = parent.querySelector('.liquid-removed')
        if (!placeholder) {
          return
        }

        placeholder.remove()
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-grid';
</style>
